import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, FormField, Grid, Heading, List, Select, Tab, Tabs, Text, TextInput } from "grommet";
import { flatten } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import { NavigationButton } from "../components/NavigationButton";
import RichTabTitle from "../components/RichTabTitle";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import ThemeBuilder from "../components/ThemeBuilder";
import { useReferenceData } from "../reference/ReferenceStore";
import { TeamRow } from "../teams/TeamsManagement";
import { getTeams, TeamViewModel } from "../teams/TeamsStore";
import { getTournament, switchSides, SwitchTournamentMatchSidesRequest, TournamentMatchViewModel, UpcomingMatches } from "../tournaments/TournamentsStore";
import MatchDetails from "./MatchDetails";
import {
    AddMatchOutcomeRequest,
    AddMatchParticipantRequest,
    addOutcome,
    addParticipant,
    getMatch,
    MatchOutcomeViewModel,
    MatchParticipantViewModel,
    MatchViewModel,
    OutcomeType,
    RemoveMatchOutcomeRequest,
    RemoveMatchParticipantRequest,
    removeOutcome,
    removeParticipant,
    updateMatch,
} from "./MatchesStore";
import MatchLogoPicker from "./MatchLogoPicker";
import MatchParticipantOutcomes from "./MatchParticipantOutcomes";
import { MatchParticipantStats } from "./MatchParticipantStats";
import MatchSplashPicker from "./MatchSplashPicker";
import { Outcome, OutcomeColor, OutcomeIcon } from "./OutcomeIcon";
import MatchResults from "./MatchResults";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;
const switchArrows = <FontAwesomeIcon icon={["fad", "arrows-left-right"]}></FontAwesomeIcon>;

const MatchProfile = () => {
    let { id } = useParams<{ id: string }>();

    let { pathname } = useLocation();
    let navigate = useNavigate();

    const [match, setMatch] = React.useState(new MatchViewModel());
    const [isLoading, setIsLoading] = React.useState(true);
    const [showSwitchSides, setShowSwitchSides] = React.useState(false);
    const [tournamentMatch, setTournamentMatch] = React.useState({} as TournamentMatchViewModel);

    React.useEffect(() => {
        if (match.id) {
            // Check if we're at the base match route (no sub-route)
            const matchIdPattern = new RegExp(`/matches/${id}/?$`);
            if (matchIdPattern.test(pathname)) {
                if (match.participants.length > 0) {
                    navigate(`teams`, { replace: true });
                } else {
                    navigate(`details`, { replace: true });
                }
            }
        }
    }, [pathname, match.id]);

    let [error, setError] = React.useState("");

    React.useEffect(() => {
        loadMatch();
    }, []);

    const loadMatch = () => {
        getMatch(id).then((match) => {
            setMatch(match);
            setParticipants(match.participants);
            setIsLoading(false);
            setIsUpdating(false);
            if (match.tournamentID) {
                getTournament(match.tournamentID).then((tournament) => {
                    let tournamentMatch = UpcomingMatches(tournament).filter((m) => m.match.id == match.id)[0];
                    setTournamentMatch(tournamentMatch);
                    if (match.participants.length == 2 && tournamentMatch && !tournamentMatch.winner) {
                        setShowSwitchSides(true);
                    }
                });
            }
        });
    };

    const [isUpdating, setIsUpdating] = React.useState(false);

    const onChange = (updatedMatch) => {
        setMatch({ ...match, ...updatedMatch });
    };

    let update = () => {
        setIsUpdating(true);
        updateMatch(match).then(
            (result) => {
                getMatch(id).then((match) => setMatch(match));
                setIsUpdating(false);
            },
            (error) => {
                setError(error);
                setIsUpdating(false);
            }
        );
    };

    const [teams, setTeams] = React.useState([] as TeamViewModel[]);

    React.useEffect(() => {
        getTeams("").then((teams) => {
            setTeams(teams);
            setTeamSearchResult(teams);
        });
    }, []);

    let [participants, setParticipants] = React.useState([] as MatchParticipantViewModel[]);

    let [teamSearchResult, setTeamSearchResult] = React.useState([] as Array<TeamViewModel>);

    const search = (filter) => {
        setTeamSearchResult(
            teams.filter((r) => {
                return r.name.toLowerCase().includes(filter.toLowerCase());
            })
        );
    };

    let [teamFilter, setTeamFilter] = React.useState("");
    const [role, setRole] = React.useState("");

    React.useEffect(() => {
        search(teamFilter);
    }, [teamFilter]);

    const [showAddParticipantModal, setShowAddParticipantModal] = React.useState(false);
    const [showRemoveParticipantModal, setShowRemoveParticipantModal] = React.useState(false);

    const [participantToRemove, setParticipantToRemove] = React.useState({} as TeamViewModel);

    const [participantCreateError, setParticipantCreateError] = React.useState("");
    const [participantRemoveError, setParticipantRemoveError] = React.useState("");

    const addParticipantToMatch = (team: TeamViewModel) => {
        setParticipantCreateError("");
        setIsLoading(true);
        addParticipant({ matchID: match.id, teamID: team.id } as AddMatchParticipantRequest).then((response) => {
            if (response.success) {
                loadMatch();
            } else {
                setParticipantCreateError(response.errors.join("\n"));
                setIsLoading(false);
            }
        });
    };

    const startRemoveParticipant = (participant: TeamViewModel) => {
        setParticipantToRemove(participant);
        setShowRemoveParticipantModal(true);
    };

    const removeParticipantFromMatch = () => {
        setParticipantRemoveError("");
        setIsLoading(true);
        removeParticipant({ matchID: match.id, teamID: participantToRemove.id } as RemoveMatchParticipantRequest).then((response) => {
            if (response.success) {
                loadMatch();
                setShowRemoveParticipantModal(false);
            } else {
                setParticipantRemoveError(response.errors.join("\n"));
                setIsLoading(false);
            }
        });
    };

    const [showRemoveAllOutcomesModal, setShowRemoveAllOutcomesModal] = React.useState(false);

    const startRemoveAllOutcomes = () => {
        setShowRemoveAllOutcomesModal(true);
    };

    const removeAllOutcomes = () => {
        Promise.all(flatten(participants.map((p) => p.outcomes)).map((o) => removeOutcome({ matchID: match.id, outcomeID: o.id } as RemoveMatchOutcomeRequest))).then(() => {
            loadMatch();
            setShowRemoveAllOutcomesModal(false);
        });
    };

    const doSwitchSides = () => {
        setIsLoading(true);
        switchSides({
            id: match.tournamentID,
            tournamentMatchID: tournamentMatch.tournamentMatchID,
        } as SwitchTournamentMatchSidesRequest).then((res) => {
            console.log(res);
            loadMatch();
        });
    };

    const hasWritePermission = usePermission("sport:matches.write");

    const [showArchiveModal, setShowArchiveModal] = React.useState(false);

    const startArchive = () => {
        setShowArchiveModal(true);
    };

    const archive = () => {
        updateMatch({ ...match, archived: !match.archived } as MatchViewModel).then(() => {
            loadMatch();
            setShowArchiveModal(false);
        });
    };

    return (
        <div>
            <SkeletonWrapper loading={isLoading}>
                <Heading size="small">
                    Match <br /> {match.archived && <Text size="small">Archived</Text>}
                </Heading>

                <Box direction="row" align="center" justify="center" gap="medium">
                    <NavigationButton href={`/matches/${match.id}/stats`} text={"Stats"}></NavigationButton>
                    <NavigationButton href={`/matches/${match.id}/teams`} text={"Teams"}></NavigationButton>
                    <NavigationButton href={`/matches/${match.id}/details`} text={"Details"}></NavigationButton>
                    <NavigationButton href={`/matches/${match.id}/results`} text={"Results"}></NavigationButton>
                    {match.tournamentID && <NavigationButton href={`/tournaments/${match.tournamentID}/bracket`} text={"Tournament"}></NavigationButton>}
                    {match.seasonID && <NavigationButton href={`/seasons/${match.seasonID}`} text={"Season"}></NavigationButton>}
                    {error && <NavigationButton href={`/matches/${match.id}/error`} text={"Errors"}></NavigationButton>}
                </Box>

                <Routes>
                    <Route
                        path={`stats`}
                        element={
                            <>
                                <Box pad={"small"} gap="small">
                                    {showSwitchSides && <Button icon={switchArrows} label="Switch Sides" onClick={doSwitchSides}></Button>}
                                    {participants.map((participant, index) => (
                                        <Box key={"s" + index} gap="small">
                                            <TeamRow team={participant.team}>
                                                <Box direction="row" gap="small"></Box>
                                            </TeamRow>
                                            <MatchParticipantStats match={match} participant={participant} disabled={!hasWritePermission} handleChange={() => loadMatch()}></MatchParticipantStats>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        }
                    />
                    <Route
                        path={`teams`}
                        element={
                            <>
                                <Box pad={"small"} gap="small">
                                    {showSwitchSides && <Button icon={switchArrows} label="Switch Sides" onClick={doSwitchSides}></Button>}
                                    {participants.map((participant, index) => (
                                        <TeamRow team={participant.team} key={index}>
                                            <Box direction="row" gap="small">
                                                <MatchParticipantOutcomes match={match} participant={participant} disabled={!hasWritePermission} handleChange={() => loadMatch()}></MatchParticipantOutcomes>
                                                {hasWritePermission && !(match.tournamentID || match.outcomes.length > 0) && (
                                                    <Button label="Remove" icon={xmark} onClick={() => startRemoveParticipant(participant.team)} color="status-critical"></Button>
                                                )}
                                            </Box>
                                        </TeamRow>
                                    ))}

                                    {hasWritePermission && match.outcomes.length > 0 && <Button label="Remove All Outcomes" icon={xmark} onClick={() => startRemoveAllOutcomes()} color="status-critical"></Button>}
                                </Box>
                                {hasWritePermission && !(match.tournamentID || match.outcomes.length > 0) && <Button icon={plus} label="Add Participant" onClick={() => setShowAddParticipantModal(true)}></Button>}
                                <StyledModal isOpen={showAddParticipantModal} setIsOpen={setShowAddParticipantModal}>
                                    <Box width={"medium"}>
                                        <FormField label="Search">
                                            <TextInput size="xs" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} />
                                        </FormField>
                                        <List
                                            pad="xs"
                                            data={teamSearchResult}
                                            itemKey="id"
                                            primaryKey={(team: TeamViewModel) => (
                                                <Text size="small" key={team.id}>
                                                    <Avatar name={team.name} src={team.logoUri} round size="25px"></Avatar> {team.name}
                                                </Text>
                                            )}
                                            action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => addParticipantToMatch(item)} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                                        ></List>
                                        <Text>
                                            <pre style={{ whiteSpace: "pre-wrap" }}>{participantCreateError}</pre>
                                        </Text>
                                    </Box>
                                </StyledModal>

                                <StyledModal isOpen={showRemoveParticipantModal} setIsOpen={setShowRemoveParticipantModal}>
                                    <Box width={"medium"}>
                                        <Text>Are you sure you want to remove this participant?</Text>
                                        <Text>
                                            <pre style={{ whiteSpace: "pre-wrap" }}>{participantRemoveError}</pre>
                                        </Text>
                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowRemoveParticipantModal(false)} label="Cancel"></Button>
                                            <Button primary onClick={() => removeParticipantFromMatch()} label="Remove" color="status-critical"></Button>
                                        </Box>
                                    </Box>
                                </StyledModal>

                                <StyledModal isOpen={showRemoveAllOutcomesModal} setIsOpen={setShowRemoveAllOutcomesModal}>
                                    <Box width={"medium"}>
                                        <Text>Are you sure you want to remove ALL outcomes from ALL participants?</Text>
                                        <Text>
                                            <pre style={{ whiteSpace: "pre-wrap" }}>{participantRemoveError}</pre>
                                        </Text>
                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowRemoveAllOutcomesModal(false)} label="Cancel"></Button>
                                            <Button primary onClick={() => removeAllOutcomes()} label="Remove" color="status-critical"></Button>
                                        </Box>
                                    </Box>
                                </StyledModal>
                            </>
                        }
                    />
                    <Route
                        path={`details`}
                        element={
                            <>
                                <MatchDetails match={match} isBusy={!hasWritePermission || isUpdating} onChange={onChange}></MatchDetails>
                                <br />
                                <Box direction="row" gap="small">
                                    {hasWritePermission && !match.tournamentID && !match.seasonID && (
                                        <>
                                            {match.archived && <Button onClick={() => startArchive()} label="Unarchive" color="status-ok"></Button>}
                                            {!match.archived && <Button onClick={() => startArchive()} label="Archive" color="status-warning"></Button>}
                                        </>
                                    )}

                                    {hasWritePermission && <Button primary onClick={update} disabled={isUpdating} label="Update"></Button>}
                                </Box>

                                <StyledModal isOpen={showArchiveModal} setIsOpen={setShowArchiveModal}>
                                    <Box width={"medium"}>
                                        {match.archived && <Text>Are you sure you want to unarchive this match?</Text>}
                                        {!match.archived && <Text>Are you sure you want to archive this match?</Text>}
                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowArchiveModal(false)} label="Cancel"></Button>
                                            {match.archived && <Button primary onClick={() => archive()} label="Unarchive" color="status-ok"></Button>}
                                            {!match.archived && <Button primary onClick={() => archive()} label="Archive" color="status-warning"></Button>}
                                        </Box>
                                    </Box>
                                </StyledModal>
                            </>
                        }
                    />
                    <Route
                        path={`results`}
                        element={<MatchResults match={match} />}
                    />
                    <Route
                        path={`errors`}
                        element={
                            <>
                                {" "}
                                <pre>{JSON.stringify(error, null, 2)}</pre>
                            </>
                        }
                    />
                </Routes>
            </SkeletonWrapper>
        </div>
    );
};

export default MatchProfile;
